import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Bell, Sun, Moon, Monitor, User, LogOut, ChevronDown, ShieldCheck } from 'lucide-react';
import { useI18n, useNamespace, SUPPORTED_LANGUAGES } from '../context/I18nContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { navigate } from '../shared/lib/router';
import { getUnreadCount, listNotifications, markNotificationRead, markAllNotificationsRead } from '../shared/api/notificationsApi';
import { search as globalSearch } from '../shared/api/searchApi';
import { ENTITY_LINK_PATHS } from '../shared/lib/entityLinks';
import { useDebouncedValue } from '../shared/lib/useDebouncedValue';
import { formatSearchResultLabel } from '../shared/lib/searchResultLabel';
import Modal from '../shared/components/Modal';

const THEME_ICONS = { light: Sun, dark: Moon, system: Monitor };

const NOTIFICATION_POLL_INTERVAL_MS = 30_000;
const SEARCH_MIN_LENGTH = 2;
const SEARCH_DROPDOWN_LIMIT = 5;

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) onOutside();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ref, onOutside]);
}

/**
 * Live cross-module search. Debounced (250ms) via the shared
 * useDebouncedValue hook, a 2-character minimum before firing at all, a
 * dropdown grouped by entity type (only non-empty types the backend
 * actually returned -- an inaccessible type is omitted server-side, not
 * sent as an empty array, but this component still only renders sections
 * that have at least one row either way), and a "see all results" footer
 * navigating to the full SearchResultsPage.
 */
function GlobalSearch() {
  const { t } = useI18n();
  useNamespace('search');
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  const debouncedTerm = useDebouncedValue(inputValue.trim(), 250);

  useEffect(() => {
    if (debouncedTerm.length < SEARCH_MIN_LENGTH) {
      setResults(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    globalSearch(debouncedTerm, SEARCH_DROPDOWN_LIMIT)
      .then((res) => {
        if (!cancelled) setResults(res.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedTerm]);

  const handleResultClick = (type, item) => {
    setOpen(false);
    const resolvedType = type === 'document' ? item.entityType : type;
    const resolvedId = type === 'document' ? item.entityId : item.id;
    const toPath = ENTITY_LINK_PATHS[resolvedType];
    if (toPath) navigate(toPath(resolvedId));
  };

  const handleSeeAll = () => {
    const term = inputValue.trim();
    if (term.length < SEARCH_MIN_LENGTH) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const resultEntries = results ? Object.entries(results).filter(([, items]) => items.length > 0) : [];
  const showDropdown = open && inputValue.trim().length >= SEARCH_MIN_LENGTH;

  return (
    <div className="app-topnav__search app-topnav__menu" ref={ref}>
      <Search size={18} aria-hidden="true" />
      <input
        type="search"
        className="app-topnav__search-input"
        placeholder={t('common', 'topnav.searchPlaceholder')}
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') handleSeeAll();
        }}
        aria-label={t('common', 'topnav.searchPlaceholder')}
      />
      {showDropdown && (
        <div className="app-topnav__dropdown app-topnav__dropdown--wide app-topnav__search-dropdown" role="menu">
          {loading && !results ? (
            <p className="empty-state">{t('common', 'actions.loading')}</p>
          ) : resultEntries.length === 0 ? (
            <p className="empty-state">{t('search', 'noResults')}</p>
          ) : (
            resultEntries.map(([type, items]) => (
              <div key={type}>
                <div className="app-topnav__dropdown-section-label">{t('search', `types.${type}`)}</div>
                <ul>
                  {items.map((item) => (
                    <li key={item.id}>
                      <button type="button" className="app-topnav__dropdown-item" onClick={() => handleResultClick(type, item)}>
                        <span>{formatSearchResultLabel(type, item)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
          <div className="app-topnav__dropdown-separator" />
          <button type="button" className="app-topnav__dropdown-item" onClick={handleSeeAll}>
            {t('search', 'seeAllResults', { term: inputValue.trim() })}
          </button>
        </div>
      )}
    </div>
  );
}

function ThemeSwitch() {
  const { t } = useI18n();
  const { theme, setTheme, themes } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  const ActiveIcon = THEME_ICONS[theme] || Monitor;

  return (
    <div className="app-topnav__menu" ref={ref}>
      <button
        type="button"
        className="app-topnav__icon-button"
        onClick={() => setOpen((prev) => !prev)}
        title={t('common', 'theme.label')}
        aria-label={t('common', 'theme.label')}
      >
        <ActiveIcon size={20} />
      </button>
      {open && (
        <ul className="app-topnav__dropdown" role="menu">
          {themes.map((option) => {
            const OptionIcon = THEME_ICONS[option];
            return (
              <li key={option}>
                <button
                  type="button"
                  className={`app-topnav__dropdown-item${theme === option ? ' app-topnav__dropdown-item--active' : ''}`}
                  onClick={() => {
                    setTheme(option);
                    setOpen(false);
                  }}
                >
                  <OptionIcon size={16} />
                  <span>{t('common', `theme.${option}`)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * The app's first instance of polling the backend -- no WebSocket/SSE infra
 * exists or is being added this phase. Polls only while authenticated
 * (TopNav only ever renders inside AppShell, i.e. post-login, but the
 * interval is still torn down on unmount defensively).
 */
function NotificationBell() {
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const refreshUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      setUnreadCount(res.data.count);
    } catch {
      // Best-effort -- a failed poll just leaves the last-known badge count.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUnreadCount]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listNotifications({ pageSize: 10 })
      .then((res) => setItems(res.data))
      .finally(() => setLoading(false));
  }, [open]);

  const handleItemClick = async (item) => {
    setOpen(false);
    if (!item.readAt) {
      markNotificationRead(item.id).catch(() => {});
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    const toPath = ENTITY_LINK_PATHS[item.entityType];
    if (toPath && item.entityId) navigate(toPath(item.entityId));
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {});
    setUnreadCount(0);
    setItems((prev) => prev.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
  };

  return (
    <div className="app-topnav__menu" ref={ref}>
      <button
        type="button"
        className="app-topnav__icon-button"
        onClick={() => setOpen((prev) => !prev)}
        title={t('common', 'topnav.notifications')}
        aria-label={t('common', 'topnav.notifications')}
      >
        <Bell size={20} />
        {unreadCount > 0 && <span className="app-topnav__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      {open && (
        <div className="app-topnav__dropdown app-topnav__dropdown--wide" role="menu">
          <div className="app-topnav__dropdown-section-label">{t('common', 'topnav.notifications')}</div>
          {loading ? (
            <p className="empty-state">{t('common', 'actions.loading')}</p>
          ) : items.length === 0 ? (
            <p className="empty-state">{t('common', 'notifications.empty')}</p>
          ) : (
            <ul>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`app-topnav__dropdown-item${!item.readAt ? ' app-topnav__dropdown-item--unread' : ''}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <span>{item.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="app-topnav__dropdown-separator" />
          <ul>
            <li>
              <button type="button" className="app-topnav__dropdown-item" onClick={handleMarkAllRead}>
                {t('common', 'notifications.markAllRead')}
              </button>
            </li>
            <li>
              <button
                type="button"
                className="app-topnav__dropdown-item"
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
              >
                {t('common', 'notifications.seeAll')}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { t, language, setLanguage } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const displayName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : '';

  return (
    <div className="app-topnav__menu" ref={ref}>
      <button
        type="button"
        className="app-topnav__user-button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <User size={18} />
        <span className="app-topnav__user-name">{displayName}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="app-topnav__dropdown app-topnav__dropdown--wide" role="menu">
          <div className="app-topnav__dropdown-section-label">{t('common', 'language.label')}</div>
          <ul>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <li key={lang}>
                <button
                  type="button"
                  className={`app-topnav__dropdown-item${language === lang ? ' app-topnav__dropdown-item--active' : ''}`}
                  onClick={() => {
                    setLanguage(lang);
                    setOpen(false);
                  }}
                >
                  {t('common', `language.${lang}`)}
                </button>
              </li>
            ))}
          </ul>
          <div className="app-topnav__dropdown-separator" />
          <ul>
            <li>
              <button
                type="button"
                className="app-topnav__dropdown-item"
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
              >
                <Bell size={16} />
                <span>{t('common', 'userMenu.notifications')}</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="app-topnav__dropdown-item"
                onClick={() => {
                  setOpen(false);
                  navigate('/security');
                }}
              >
                <ShieldCheck size={16} />
                <span>{t('common', 'userMenu.security')}</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="app-topnav__dropdown-item app-topnav__dropdown-item--danger"
                onClick={() => {
                  setOpen(false);
                  setConfirmingLogout(true);
                }}
              >
                <LogOut size={16} />
                <span>{t('common', 'userMenu.logout')}</span>
              </button>
            </li>
          </ul>
        </div>
      )}

      {confirmingLogout && (
        <Modal
          title={t('common', 'userMenu.confirmLogoutTitle')}
          onClose={() => setConfirmingLogout(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmingLogout(false)}>
                {t('common', 'actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  setConfirmingLogout(false);
                  // logout() itself handles clearing the session AND
                  // navigating away with the tenant slug preserved -- see
                  // AuthContext.jsx for why that's centralized there rather
                  // than duplicated at each call site (the auto-logout
                  // timer is the other one).
                  logout();
                }}
              >
                <LogOut size={16} /> {t('common', 'userMenu.logout')}
              </button>
            </>
          }
        >
          <p>{t('common', 'userMenu.confirmLogoutMessage')}</p>
        </Modal>
      )}
    </div>
  );
}

/**
 * Top navigation bar: live global search (see GlobalSearch above), the
 * notification bell (polls for unread count, see NotificationBell above),
 * the theme switcher, and the user menu (language switcher, profile link,
 * logout).
 */
export default function TopNav() {
  return (
    <header className="app-topnav">
      <GlobalSearch />
      <div className="app-topnav__actions">
        <NotificationBell />
        <ThemeSwitch />
        <UserMenu />
      </div>
    </header>
  );
}
